import React from 'react';
import { render } from 'react-dom';
import { match, browserHistory } from 'react-router';
import qs from 'query-string';
import domready from 'domready';
import debugLib from 'debug';
import _ from 'lodash';
import { createApp } from './app';
import config from './config';
import resolveRoutesLoadActions from './util/resolveRoutesLoadActions';
import { scrollListener, resizeListener } from './util/eventListeners';
import routesGenerator from './util/routesGenerator';
import updateLocation from './action/updateLocation';
import updateRouteParams from './action/updateRouteParams';
import updateSovSeed from './action/updateSovSeed';
import ViasportApp from './component/Page/ViasportApp.jsx';
import extractKruxData from './util/extractKruxData';
import pageTracking from './util/pageTracking';
import dataLayer from 'service/dataLayer';
import googleTagManagerConfig from 'config/googleTagManager';

const { APP_STATE_PROPERTY, APP_MOUNT_ID, DEBUG } = config;

// initial serialized state from server - needed for isomorphism
const app = createApp(window[APP_STATE_PROPERTY]);

// exposed for easier debugging
window.React = React;
window.config = config;
window.store = app.store;

// expose debug on window so we can enable/disable it in the browser
window.debugLib = debugLib;

if (DEBUG) {
  debugLib.enable(DEBUG);
}

domready(() => {
  // Init GTM DataLayer
  if (typeof window !== 'undefined') {
    const dl = googleTagManagerConfig.config.dataLayer || 'dataLayer';
    if (window[dl]) {
      dataLayer.init(window[dl].push.bind(window[dl]));
    }
  }

  // setup some global event listeners that trigger Redux actions
  scrollListener(app.store.dispatch, 100);
  resizeListener(app.store.dispatch, 200);

  let initialRender = true;
  const country = app.store.getState().country;
  const routes = routesGenerator(country);

  browserHistory.listen(location => {
    // match() is normally only used on server, but we are using it on the client as well to
    // handle async data loading (our so called loadActions)
    match(
      { routes, location, history: browserHistory },
      (error, redirectLocation, renderProps) => {
        // The danish ad script ads this class when there's a takeover ad, if navigating to another page
        // after that doesnt have a takeover ad the class still remains..
        if (location.action === 'PUSH' && country === 'dk') {
          const adsmWallpaper = document.getElementsByClassName(
            'adsm-wallpaper',
          )[0];
          if (adsmWallpaper) document.body.removeChild(adsmWallpaper);

          document.body.parentNode.classList.remove('adsm-skin');
        }

        // if we found a redirect route (<Redirect /> or <IndexRedirect />) just perform the redirect and then abort
        if (redirectLocation) {
          if (location.action !== 'REPLACE') pageTracking(country);
          browserHistory.replace(redirectLocation);
          return;
        }

        extractKruxData(country);

        // If we dont render before we dispatch routeLoadActions, the old component will rerender in vain
        // <ViasportApp> is a thin wrapper to setup Redux and React Router
        render(
          <ViasportApp app={app} renderProps={renderProps} />,
          document.getElementById(APP_MOUNT_ID),
        );

        // Track after render to use updated title
        if (location.action !== 'REPLACE') pageTracking(country);

        // for each matched component, fire (but don't wait for resolve) its loadActions
        if (!initialRender) {
          app.store.dispatch(
            updateRouteParams({ params: renderProps.params, country }),
          );

          // don't dispatch an UPDATE_LOCATION action when the app is rendered for the first time on the client
          app.store.dispatch(updateLocation(location));

          const host = `${window.location.protocol}//${window.location.host}`;
          resolveRoutesLoadActions(
            app.store.dispatch,
            renderProps.components,
            location.pathname,
            renderProps.params,
            qs.parse(renderProps.location.search),
            country,
            app.store.getState().language,
            host,
            app.store,
            location.action === 'POP',
          );
        }

        // Update SOV (Share of Voice) seed to be able to get ads
        // from a single advertiser on every location change
        app.store.dispatch(updateSovSeed(_.random(1, 100).toString()));

        // Trigger google Optimize
        if (window.triggerOptimize) {
          window.triggerOptimize();
        }

        // Ensure that position is always (0,0) when navigating forward.
        window.scrollTo(0, 0);

        if (initialRender) {
          initialRender = false;
        }
      },
    );
  });
});



// WEBPACK FOOTER //
// ./src/client.js